import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { Alert, Button, Card, Checkbox, Col, Divider, Form, Input, InputNumber, Modal, Row } from "antd";
import dayjs, { Dayjs } from "dayjs";
import * as React from "react";
import { DatePicker, Select } from "src/components";
import { toBangkokNaive } from "src/utils";
import {
  AddonType,
  CAMPAIGN_TYPE_LABEL_TH,
  CampaignDetail,
  CampaignStatus,
  CampaignType,
  CampaignUpdateRequest,
  useGetAffiliateCampaignDetail,
  useUpdateAffiliateCampaign,
} from "src/hooks/affiliate";

interface TierFormItem {
  tierNumber: number;
  commissionRate: number;
}
interface AddonFormItem {
  addonType: AddonType;
  minValue?: number;
  maxValue?: number;
  limitPerUser?: number;
  commissionRate: number;
}
interface EditFormValues {
  campaignName: string;
  status: CampaignStatus;
  type: CampaignType;
  dateRange: [Dayjs, Dayjs];
  commissionLimit?: number;
  isActive?: boolean;
  iconUrl?: string;
  description?: string;
  tiers?: TierFormItem[];
  addons?: AddonFormItem[];
}

const statusOptions = [
  { value: CampaignStatus.DRAFT, label: "DRAFT" },
  { value: CampaignStatus.ACTIVE, label: "ACTIVE" },
  { value: CampaignStatus.INACTIVE, label: "INACTIVE" },
  { value: CampaignStatus.COMPLETED, label: "COMPLETED" },
];

const campaignTypeOptions = Object.values(CampaignType).map((v) => ({
  value: v,
  label: CAMPAIGN_TYPE_LABEL_TH[v],
}));

const addonTypeOptions = [
  { value: AddonType.REFERRAL_COUNT, label: "REFERRAL_COUNT" },
  { value: AddonType.SALE_VOLUME, label: "SALE_VOLUME" },
  { value: AddonType.SEGMENT_BASED, label: "SEGMENT_BASED" },
];

export type AffiliateCampaignEditModalProps = {
  campaign: CampaignDetail;
  onSuccess?: () => void;
};

export const AffiliateCampaignEditModal = NiceModal.create(
  ({ campaign, onSuccess }: AffiliateCampaignEditModalProps) => {
    const { visible, hide, remove } = useModal();
    const [form] = Form.useForm<EditFormValues>();

    // ล็อก (แก้ได้แค่ icon) เฉพาะเมื่อ status = ACTIVE และกำลังรัน (ตอนนี้อยู่ในช่วง
    // [start_date, end_date]). ถ้า status ไม่ใช่ ACTIVE → แก้ไขได้เต็มเสมอ.
    const now = dayjs().valueOf();
    const started = dayjs(campaign.startDate).valueOf() <= now;
    const ended = campaign.endDate ? now > dayjs(campaign.endDate).valueOf() : false;
    const locked = campaign.status === CampaignStatus.ACTIVE && started && !ended;

    // ดึง tier/addon ปัจจุบันมา prefill (เฉพาะตอนแก้ได้เต็ม)
    const { data: detail } = useGetAffiliateCampaignDetail(locked ? "" : campaign.campaignCode);
    React.useEffect(() => {
      if (!detail) return;
      form.setFieldsValue({
        tiers: (detail.tierConfig || []).map((t) => ({
          tierNumber: t.tierNumber,
          commissionRate: t.commissionRate,
        })),
        addons: (detail.addonConfig || []).map((a) => ({
          addonType: a.addonType,
          minValue: a.minValue ?? undefined,
          maxValue: a.maxValue ?? undefined,
          limitPerUser: a.limitPerUser ?? undefined,
          commissionRate: a.commissionRate,
        })),
      });
    }, [detail, form]);

    const { mutate: updateCampaign, isLoading } = useUpdateAffiliateCampaign({
      onSuccess: () => {
        onSuccess?.();
        hide();
      },
    });

    const handleFinish = (values: EditFormValues) => {
      const iconUrl = values.iconUrl?.trim() ? values.iconUrl.trim() : null;

      if (locked) {
        updateCampaign({ campaignCode: campaign.campaignCode, iconUrl });
        return;
      }

      const [start, end] = values.dateRange || [];
      const payload: { campaignCode: string } & CampaignUpdateRequest = {
        campaignCode: campaign.campaignCode,
        campaignName: values.campaignName,
        description: values.description?.trim() || null,
        startDate: start ? toBangkokNaive(start) : undefined,
        endDate: end ? toBangkokNaive(end) : undefined,
        status: values.status,
        type: values.type,
        isActive: values.isActive,
        commissionLimit: values.commissionLimit != null ? String(values.commissionLimit) : null,
        iconUrl,
        tiers: (values.tiers || []).map((t) => ({
          tierNumber: Number(t.tierNumber),
          commissionRate: String(Number(t.commissionRate)),
        })),
        addons: (values.addons || []).map((a) => ({
          addonType: a.addonType,
          minValue: a.minValue ?? null,
          maxValue: a.maxValue ?? null,
          limitPerUser: a.limitPerUser ?? null,
          commissionRate: String(Number(a.commissionRate)),
        })),
      };
      updateCampaign(payload);
    };

    return (
      <Modal
        centered
        width={800}
        visible={visible}
        afterClose={remove}
        closable={false}
        title={`แก้ไข Campaign — ${campaign.campaignCode}`}
        footer={
          <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
            <Button key="cancel" size="large" onClick={hide} style={{ width: 100 }}>
              ยกเลิก
            </Button>
            <Button
              key="submit"
              type="primary"
              size="large"
              loading={isLoading}
              onClick={() => form.submit()}
              style={{ width: 160 }}
            >
              บันทึก
            </Button>
          </div>
        }
      >
        {locked && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Campaign นี้ Active และกำลังรันอยู่ (อยู่ในช่วงเวลา) แก้ไขได้เฉพาะ Icon URL เท่านั้น"
          />
        )}
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          onFinish={handleFinish}
          preserve={false}
          initialValues={{
            campaignName: campaign.campaignName,
            status: campaign.status,
            type: campaign.type ?? CampaignType.COMMISSION,
            dateRange: [dayjs(campaign.startDate), dayjs(campaign.endDate)],
            commissionLimit: campaign.commissionLimit ?? undefined,
            isActive: campaign.isActive,
            iconUrl: campaign.iconUrl ?? "",
            description: campaign.description ?? "",
            tiers: [],
            addons: [],
          }}
          style={{ maxHeight: "65vh", overflowY: "auto", paddingRight: 8 }}
        >
          <Card title="ข้อมูลพื้นฐาน" size="small">
            <Row gutter={[16, 0]}>
              <Col span={16}>
                <Form.Item
                  label="ชื่อ Campaign"
                  name="campaignName"
                  rules={[{ required: true, message: "กรุณาใส่ชื่อ campaign" }, { max: 255 }]}
                >
                  <Input disabled={locked} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="สถานะ" name="status" rules={[{ required: true }]}>
                  <Select options={statusOptions} disabled={locked} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="ช่วงเวลา campaign" name="dateRange" rules={[{ required: true }]}>
                  <DatePicker.RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY HH:mm"
                    showTime={{ format: "HH:mm" }}
                    disabled={locked}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="ประเภท Campaign" name="type" rules={[{ required: true }]}>
                  <Select options={campaignTypeOptions} disabled={locked} />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item name="isActive" valuePropName="checked" label=" ">
                  <Checkbox disabled={locked}>Active</Checkbox>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label="วงเงิน Commission" name="commissionLimit">
                  <InputNumber min={0} style={{ width: "100%" }} disabled={locked} />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item label="Icon URL" name="iconUrl">
                  <Input placeholder="https://... (เว้นว่างเพื่อลบ)" allowClear />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item label="คำอธิบาย" name="description">
                  <Input.TextArea rows={2} disabled={locked} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {!locked && (
            <>
              <Divider />
              <Card title="Tier Config" size="small">
                <Form.List name="tiers">
                  {(fields, { add, remove: removeTier }) => (
                    <>
                      {fields.map(({ key, name, ...rest }) => (
                        <Row gutter={[16, 0]} key={key} align="middle">
                          <Col span={8}>
                            <Form.Item
                              {...rest}
                              label="Tier Number"
                              name={[name, "tierNumber"]}
                              rules={[{ required: true, message: "ระบุ tier number" }]}
                            >
                              <InputNumber min={1} style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              {...rest}
                              label="Commission Rate (%)"
                              name={[name, "commissionRate"]}
                              rules={[{ required: true, message: "ระบุ commission rate" }]}
                            >
                              <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <MinusCircleOutlined onClick={() => removeTier(name)} />
                          </Col>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                        เพิ่ม Tier
                      </Button>
                    </>
                  )}
                </Form.List>
              </Card>

              <Divider />
              <Card title="Addon Config" size="small">
                <Form.List name="addons">
                  {(fields, { add, remove: removeAddon }) => (
                    <>
                      <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: 720 }}>
                          {fields.map(({ key, name, ...rest }) => (
                            <Row gutter={[16, 0]} key={key} align="middle" wrap={false}>
                              <Col span={5}>
                                <Form.Item
                                  {...rest}
                                  label="Addon Type"
                                  name={[name, "addonType"]}
                                  rules={[{ required: true, message: "เลือก addon type" }]}
                                >
                                  <Select options={addonTypeOptions} width="100%" />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item {...rest} label="Min" name={[name, "minValue"]}>
                                  <InputNumber min={0} style={{ width: "100%" }} />
                                </Form.Item>
                              </Col>
                              <Col span={4}>
                                <Form.Item {...rest} label="Max" name={[name, "maxValue"]}>
                                  <InputNumber min={0} style={{ width: "100%" }} />
                                </Form.Item>
                              </Col>
                              <Col span={5}>
                                <Form.Item {...rest} label="Limit/User (บาท/วัน)" name={[name, "limitPerUser"]}>
                                  <InputNumber min={0} style={{ width: "100%" }} />
                                </Form.Item>
                              </Col>
                              <Col span={5}>
                                <Form.Item
                                  {...rest}
                                  label="Rate (%)"
                                  name={[name, "commissionRate"]}
                                  rules={[{ required: true, message: "ระบุ rate" }]}
                                >
                                  <InputNumber min={0} step={0.01} style={{ width: "100%" }} />
                                </Form.Item>
                              </Col>
                              <Col span={1}>
                                <MinusCircleOutlined onClick={() => removeAddon(name)} />
                              </Col>
                            </Row>
                          ))}
                        </div>
                      </div>
                      <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />} block>
                        เพิ่ม Addon
                      </Button>
                    </>
                  )}
                </Form.List>
              </Card>
            </>
          )}
        </Form>
      </Modal>
    );
  },
);

export default AffiliateCampaignEditModal;
